import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  Card,
  EmptyState,
  FilterBar,
  InlineStats,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { formatNumber } from '../../components/admin/labels';
import { IconMerchants } from '../../components/admin/icons';
import { UpgradeGate, WindowPicker } from '../../components/courier/ui';
import { useCourierSession } from '../../components/courier/CourierLayout';

/**
 * Which retailers the reports against this carrier cluster around.
 *
 * A share-of-total bar sits behind each row: the question a courier actually
 * has here is "how concentrated is this", and a column of raw counts answers
 * that far more slowly than a bar does.
 */
const CourierMerchants = () => {
  const toast = useToast();
  const { me, fullAccess } = useCourierSession() || {};

  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!fullAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/api/courier-portal/merchants?days=${days}`);
      setData(payload);
    } catch (error) {
      if (error.response?.data?.code !== 'UPGRADE_REQUIRED') {
        toast('Could not load the retailer breakdown.', { tone: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [days, fullAccess, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (!fullAccess) {
    return (
      <UpgradeGate
        title="See which retailers your issues cluster around"
        description="Reports rarely spread evenly. Intelligence shows which retailers' deliveries generate them, and what goes wrong most often for each."
        features={me?.pricing?.courierPlanPrices?.intelligence?.features || []}
        price={me?.pricing?.courierPlanPrices?.intelligence?.price ?? null}
      />
    );
  }

  const rows = data?.merchants || [];
  const total = rows.reduce((sum, r) => sum + r.cases, 0);
  const max = Math.max(...rows.map((r) => r.cases), 1);

  return (
    <Card>
      <FilterBar
        actions={
          <>
            <InlineStats
              items={[
                { label: 'Retailers', value: loading ? '—' : formatNumber(rows.length) },
                { label: 'Reports', value: loading ? '—' : formatNumber(total) }
              ]}
            />
            <WindowPicker days={days} onChange={setDays} onRefresh={load} loading={loading} />
          </>
        }
      />

      {loading ? (
        <TableSkeleton rows={8} cols={4} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={IconMerchants}
          title="No retailer breakdown for this period"
          description={`Nothing has been reported against your network in the last ${days} days.`}
        />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Retailer</Th>
              <Th>Most reported</Th>
              <Th className="w-[34%]">Share</Th>
              <Th className="text-right">Reports</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <Tr key={row.name}>
                <Td className="tabular-nums text-ink-500">{i + 1}</Td>
                <Td className="font-600 text-ink-900">{row.name}</Td>
                <Td className="text-ink-600">{row.topIssue || '—'}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                      <div
                        className="h-full rounded-full bg-info-500"
                        style={{ width: `${Math.round((row.cases / max) * 100)}%` }}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-ink-500">
                      {total ? Math.round((row.cases / total) * 100) : 0}%
                    </span>
                  </div>
                </Td>
                <Td className="text-right font-600 tabular-nums text-ink-900">
                  {formatNumber(row.cases)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Card>
  );
};

export default CourierMerchants;
